import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SmoothScroll, useScrollToTopOnRoute } from "@/components/SmoothScroll";
import { ScrollProgress } from "@/components/ScrollProgress";
import { WhatsAppFab } from "@/components/WhatsAppFab";
import { ConsentProvider } from "@/components/consent/ConsentProvider";

import Home from "@/pages/Home";
import ChiSiamo from "@/pages/ChiSiamo";
import Servizi from "@/pages/Servizi";
import Settori from "@/pages/Settori";
import SettoreDetail from "@/pages/SettoreDetail";
import Contatti from "@/pages/Contatti";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import InformazioniSocietarie from "@/pages/InformazioniSocietarie";
import Reclami from "@/pages/Reclami";
import Privacy from "@/pages/Privacy";
import Cookie from "@/pages/Cookie";
import Assistart from "@/pages/Assistart";
import RCTEdilizia from "@/pages/RCTEdilizia";
import RCProfessionale from "@/pages/RCProfessionale";

function ScrollToTop() {
  useScrollToTopOnRoute();
  const { pathname } = useLocation();
  useEffect(() => {
    const titles = {
      "/": "XL Brokers — Your Insurance Partners",
      "/chi-siamo": "Chi Siamo — XL Brokers",
      "/servizi": "Servizi — XL Brokers",
      "/settori": "Settori — XL Brokers",
      "/contatti": "Contatti — XL Brokers",
      "/accedi": "Area Riservata — XL Brokers",
      "/registrati": "Registrati — XL Brokers",
      "/area-riservata": "Dashboard — XL Brokers",
      "/informazioni-societarie": "Informazioni Intermediario — XL Brokers",
      "/informazioni-intermediario": "Informazioni Intermediario — XL Brokers",
      "/reclami": "Reclami — XL Brokers",
      "/privacy": "Privacy Policy — XL Brokers",
      "/cookie": "Cookie Policy — XL Brokers",
      "/assistart": "Assistart — Podcast XL Brokers",
      "/rct-edilizia": "Assicurazioni RC Edilizia e Artigiani — Consulenza su Misura | XL Brokers",
      "/lp/rct-edilizia": "Assicurazioni RC Edilizia e Artigiani — Consulenza su Misura | XL Brokers",
      "/lp/rc-professionale-commercialisti-avvocati": "RC Professionale Commercialisti, Consulenti del Lavoro e Avvocati | XL Brokers",
      "/lp/rc-professionale-tecnici": "RC Professionale Architetti, Ingegneri e Geometri | XL Brokers",
      "/lp/rc-professionale-medici": "RC Sanitaria: Medici, Odontoiatri, Poliambulatori e Centri Odontoiatrici | XL Brokers",
    };
    const exact = titles[pathname];
    if (exact) document.title = exact;
    else if (pathname.startsWith("/settori/")) {
      const slug = pathname.split("/")[2] || "";
      const pretty = slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      document.title = `${pretty} — XL Brokers`;
    } else document.title = "XL Brokers";

    const DEFAULT_DESC =
      "XL Brokers — Società di brokeraggio assicurativo a Bologna. Coperture su misura per PMI, professionisti e famiglie.";
    const descriptions = {
      "/rct-edilizia":
        "Polizza RC e coperture su misura per imprese edili, idraulici, elettricisti, artigiani e installatori. XL Brokers, broker indipendente: analisi del rischio e consulenza dedicata, senza impegno.",
      "/lp/rct-edilizia":
        "Polizza RC e coperture su misura per imprese edili, idraulici, elettricisti, artigiani e installatori. XL Brokers, broker indipendente: analisi del rischio e consulenza dedicata, senza impegno.",
      "/lp/rc-professionale-commercialisti-avvocati":
        "RC Professionale per commercialisti, consulenti del lavoro, avvocati e revisori. Coperture su misura, retroattività e postuma. XL Brokers, broker indipendente: consulenza dedicata senza impegno.",
      "/lp/rc-professionale-tecnici":
        "RC Professionale per architetti, ingegneri, geometri e coordinatori sicurezza. Progettazione, direzione lavori, postuma decennale. XL Brokers, broker indipendente: consulenza dedicata senza impegno.",
      "/lp/rc-professionale-medici":
        "RC in ambito sanitario per medici e odontoiatri liberi professionisti, poliambulatori e centri odontoiatrici. Conforme alla Legge Gelli-Bianco, formula ALL RISK. XL Brokers, broker indipendente: consulenza senza impegno.",
    };
    const desc = descriptions[pathname] || DEFAULT_DESC;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, [pathname]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const isAuthPage = pathname === "/accedi" || pathname === "/registrati";
  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/chi-siamo" element={<ChiSiamo />} />
          <Route path="/servizi" element={<Servizi />} />
          <Route path="/settori" element={<Settori />} />
          <Route path="/settori/:slug" element={<SettoreDetail />} />
          <Route path="/contatti" element={<Contatti />} />
          <Route path="/accedi" element={<Login />} />
          <Route path="/registrati" element={<Register />} />
          <Route path="/informazioni-societarie" element={<InformazioniSocietarie />} />
          <Route path="/informazioni-intermediario" element={<InformazioniSocietarie />} />
          <Route path="/reclami" element={<Reclami />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookie" element={<Cookie />} />
          <Route path="/assistart" element={<Assistart />} />
          <Route path="/rct-edilizia" element={<RCTEdilizia />} />
          <Route path="/lp/rct-edilizia" element={<RCTEdilizia />} />
          <Route path="/lp/rc-professionale-commercialisti-avvocati" element={<RCProfessionale slug="rc-professionale-commercialisti-avvocati" />} />
          <Route path="/lp/rc-professionale-tecnici" element={<RCProfessionale slug="rc-professionale-tecnici" />} />
          <Route path="/lp/rc-professionale-medici" element={<RCProfessionale slug="rc-professionale-medici" />} />
          <Route
            path="/area-riservata"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      {!isAuthPage && <Footer />}
      <WhatsAppFab />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SmoothScroll>
            <ConsentProvider>
              <ScrollToTop />
              <ScrollProgress />
              <Shell />
              <Toaster position="bottom-left" />
            </ConsentProvider>
          </SmoothScroll>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
